<script setup>
import { ref, computed, getCurrentInstance, onMounted } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import ExternalLayout from '@/layout/external/ExternalLayout.vue';
import { useLoading } from '@/composables/useLoading';
import { useToast } from 'primevue/usetoast';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import InputText from 'primevue/inputtext';
import Dialog from 'primevue/dialog';
import Divider from 'primevue/divider';
import SiteFeaturesInfo from '@/components/SiteFeaturesInfo.vue';

defineOptions({
    layout: ExternalLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();
const toast = useToast();
const page = usePage();

const props = defineProps({
    order: {
        type: Object,
        required: true
    },
    itemsWithContent: {
        type: Array,
        default: () => []
    },
    allConfirmed: {
        type: Boolean,
        default: false
    },
    hasPublishedContent: {
        type: Boolean,
        default: false
    },
    token: {
        type: String,
        required: true
    }
});

const liveLinks = ref({});
const showErrorModal = ref(false);
const errorMessage = ref('');

const expandedItems = ref({});

const translateError = (errorCode) => {
    if (errorCode.startsWith('domain_mismatch:')) {
        const parts = errorCode.split(':');
        const correctDomain = parts[1];
        const providedDomain = parts[2] || '';
        return $t('The live link domain does not match the purchased site domain') + 
               ` (${$t('Expected')}: ${correctDomain}, ${$t('Provided')}: ${providedDomain})`;
    } else if (errorCode.startsWith('domain_similar:')) {
        const parts = errorCode.split(':');
        const correctDomain = parts[1];
        const providedDomain = parts[2] || '';
        return $t('The live link domain is similar to but not identical to the purchased domain') + 
               ` (${$t('Expected')}: ${correctDomain}, ${$t('Provided')}: ${providedDomain})`;
    } else if (errorCode === 'domain_mismatch') {
        return $t('The live link domain must match the purchased site domain');
    }

    else if (errorCode.startsWith('scraping_failed:')) {
        const statusCode = errorCode.split(':')[1];
        return $t('Failed to access the live link page') + ` (${$t('Status')}: ${statusCode})`;
    } else if (errorCode.startsWith('scraping_exception:')) {
        return $t('Error occurred while checking the live link page');
    }

    else if (errorCode.startsWith('anchor_not_found|')) {
        const parts = errorCode.split('|');
        const anchorName = parts[1] || '';
        const anchorUrl = parts[2] || '';
        return $t('Neither anchor text nor URL were found on the page') + 
               ` (${$t('Text')}: "${anchorName}", ${$t('URL')}: "${anchorUrl}")`;
    }
    
    else if (errorCode.startsWith('anchor_name_similar|')) {
        const parts = errorCode.split('|');
        const expectedName = parts[1] || '';
        const foundSimilar = parts[2] || '';
        return $t('Exact anchor text not found, but similar text was found') + 
               ` (${$t('Expected')}: "${expectedName}", ${$t('Found similar')}: "${foundSimilar}")`;
    } else if (errorCode.startsWith('anchor_url_similar|')) {
        const parts = errorCode.split('|');
        const expectedUrl = parts[1] || '';
        const foundSimilar = parts[2] || '';
        return $t('Exact anchor URL not found, but similar URL was found') + 
               ` (${$t('Expected')}: "${expectedUrl}", ${$t('Found similar')}: "${foundSimilar}")`;
    }
    
    else if (errorCode.startsWith('anchor_both_similar|')) {
        const parts = errorCode.split('|');
        const expectedName = parts[1] || '';
        const foundSimilarName = parts[2] || '';
        const expectedUrl = parts[3] || '';
        const foundSimilarUrl = parts[4] || '';
        return $t('Exact anchor tag not found, but similar text and URL were found') + 
               ` (${$t('Expected text')}: "${expectedName}", ${$t('Found similar text')}: "${foundSimilarName}", ` +
               `${$t('Expected URL')}: "${expectedUrl}", ${$t('Found similar URL')}: "${foundSimilarUrl}")`;
    }

    else if (errorCode.startsWith('anchor_name_not_found|')) {
        const parts = errorCode.split('|');
        const expectedName = parts[1] || '';
        const status = parts[2] || '';
        if (status === 'not_found') {
            return $t('Anchor text not found on the page') + 
                   ` (${$t('Expected')}: "${expectedName}", ${$t('Found')}: ${$t('Nothing')})`;
        } else {
            return $t('Anchor text not found on the page') + ` ("${expectedName}")`;
        }
    } else if (errorCode.startsWith('anchor_url_not_found|')) {
        const parts = errorCode.split('|');
        const expectedUrl = parts[1] || '';
        const status = parts[2] || '';
        if (status === 'not_found') {
            return $t('Anchor URL not found on the page') + 
                   ` (${$t('Expected')}: "${expectedUrl}", ${$t('Found')}: ${$t('Nothing')})`;
        } else {
            return $t('Anchor URL not found on the page') + ` ("${expectedUrl}")`;
        }
    }

    else if (errorCode === 'anchor_mismatch') {
        return $t('Anchor tag found but does not match the required text and URL');
    }

    else if (errorCode.startsWith('validation_exception:')) {
        return $t('Validation error occurred. Please try again.');
    }
    
    return errorCode;
};

const getErrorSeverity = (errorCode) => {
    if (errorCode.startsWith('domain_similar:') || 
        errorCode.startsWith('anchor_name_similar|') || 
        errorCode.startsWith('anchor_url_similar|') || 
        errorCode.startsWith('anchor_both_similar|')) {
        return 'warn';
    }

    return 'error';
};

const openErrorModal = (message) => {
    errorMessage.value = message;
    showErrorModal.value = true;
};

const closeErrorModal = () => {
    showErrorModal.value = false;
    errorMessage.value = '';
};

onMounted(() => {
    props.itemsWithContent?.forEach(item => {
        liveLinks.value[item.id] = item.link || '';
    });
});

const confirmLiveLink = async (itemId) => {
    const liveLink = liveLinks.value[itemId];

    if (!liveLink || !liveLink.trim()) {
        openErrorModal($t('Please enter a valid live link URL'));
        return;
    }

    try {
        new URL(liveLink);
    } catch (e) {
        openErrorModal($t('Please enter a valid URL (must start with http:// or https://)'));
        return;
    }    router.post(
        `/external/live-link/${props.token}/confirm`,
        {
            item_id: itemId,
            link: liveLink,
        },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Confirming live link...')),
            onFinish: () => hideLoading(),
            onError: (errors) => {
                hideLoading();
            },
            onSuccess: (page) => {
                const item = props.itemsWithContent.find(i => i.id === itemId);
                if (item) {
                    item.live_link_confirmed_at = new Date().toISOString();
                    item.link = liveLink;
                }
            }
        }
    );
};

const itemsCount = computed(() => props.itemsWithContent?.length || 0);
const confirmedCount = computed(() => props.itemsWithContent?.filter(item => item.live_link_confirmed_at !== null).length || 0);

const toggleItemExpansion = (itemId) => {
    expandedItems.value[itemId] = !expandedItems.value[itemId];
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
};
</script>

<template>
    <div class="grid">
        <!-- Header Card -->
        <Card class="mb-6">
            <template #header>
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between p-6 pb-0 gap-4">
                    <div>
                        <h1 class="text-2xl font-bold text-surface-900">
                            {{ $t('Live Link Confirmation') }}
                        </h1>
                        <p class="text-surface-600 mt-1">
                            {{ $t('Order') }} #{{ order.number }} - {{ order.client?.name }}
                        </p>
                        <p class="text-sm text-surface-500 mt-2">
                            {{ $t('Please confirm your live links by entering the URLs where your content was published.') }}
                        </p>
                    </div>
                    <div class="flex items-center gap-3">
                        <div class="text-center">
                            <div class="text-2xl font-bold text-primary-600">{{ confirmedCount }}/{{ itemsCount }}</div>
                            <div class="text-sm text-surface-600">{{ $t('Confirmed') }}</div>
                        </div>
                    </div>
                </div>
            </template>

            <template #content>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                                <i class="pi pi-list text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Published Content') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ itemsCount }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                <i class="pi pi-check text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Live Links Confirmed') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ confirmedCount }}/{{ itemsCount }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- No Published Content Message -->
        <Card v-if="!hasPublishedContent" class="mb-6">
            <template #content>
                <div class="text-center py-12">
                    <i class="pi pi-inbox text-6xl text-surface-300 mb-4"></i>
                    <h3 class="text-xl font-semibold text-surface-700 mb-2">
                        {{ $t('No published content available') }}
                    </h3>
                    <p class="text-surface-500">
                        {{ $t('There is no content available for live link confirmation at this time.') }}
                    </p>
                </div>
            </template>
        </Card>

        <!-- Items List -->
        <div v-else class="space-y-4">
            <Card v-for="(item, index) in itemsWithContent" :key="item.id">
                <template #header>
                    <div class="p-6 pb-0">
                        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                            <div class="flex items-center gap-3">
                                <div class="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                                    <span class="font-bold text-primary-700">{{ index + 1 }}</span>
                                </div>
                                <div>
                                    <h3 class="font-semibold text-surface-900">
                                        {{ item.site_url || $t('Site URL not available') }}
                                    </h3>
                                    <p class="text-sm text-surface-600">
                                        {{ item.type_name || '-' }}
                                    </p>
                                </div>
                            </div>
                            <div class="flex items-center gap-3">
                                <Tag
                                    v-if="item.live_link_confirmed_at"
                                    :value="$t('Live Link Confirmed')"
                                    severity="success"
                                    icon="pi pi-check"
                                />
                                <Tag
                                    v-else
                                    :value="$t('Pending Live Link')"
                                    severity="warn"
                                    icon="pi pi-clock"
                                />
                                
                                <Button
                                    :icon="expandedItems[item.id] ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                                    :label="$t('Show')"
                                    size="small"
                                    severity="secondary"
                                    outlined
                                    @click="toggleItemExpansion(item.id)"
                                />
                            </div>
                        </div>
                    </div>
                </template>

                <template #content>
                    <div v-show="expandedItems[item.id]">
                        <div v-if="item.content" class="space-y-4">
                            <!-- Site Information Section -->
                            <SiteFeaturesInfo v-if="item.site_features" :features="item.site_features" :accepted-anchors="item.site_features?.accepted_anchors || item.accepted_anchors || 1" />

                            <!-- Anchors Section -->
                            <div v-if="item.content.anchor_name || item.content.anchor_url || item.content.anchor_name_2 || item.content.anchor_url_2 || item.content.anchor_name_3 || item.content.anchor_url_3" 
                                 class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <h5 class="text-sm font-semibold text-blue-900 mb-3">
                                    <i class="pi pi-link text-blue-700 mr-2"></i>
                                    {{ $t('Required Anchors') }}
                                </h5>
                                
                                <div class="flex flex-col md:flex-row gap-4">
                                    <!-- Anchor 1 -->
                                    <div v-if="item.content.anchor_name || item.content.anchor_url" class="flex-1">
                                        <div class="space-y-2">
                                            <div>
                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                    {{ $t('Anchor Name') }} 1
                                                </label>
                                                <p class="text-sm text-blue-900">{{ item.content.anchor_name || '-' }}</p>
                                            </div>
                                            <div>
                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                    {{ $t('Anchor URL') }} 1
                                                </label>
                                                <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url || '-' }}</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <!-- Divider -->
                                    <Divider v-if="(item.content.anchor_name || item.content.anchor_url) && (item.content.anchor_name_2 || item.content.anchor_url_2)" layout="vertical" class="hidden md:flex" />
                                    
                                    <!-- Anchor 2 -->
                                    <div v-if="item.content.anchor_name_2 || item.content.anchor_url_2" class="flex-1">
                                        <div class="space-y-2">
                                            <div>
                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                    {{ $t('Anchor Name') }} 2
                                                </label>
                                                <p class="text-sm text-blue-900">{{ item.content.anchor_name_2 || '-' }}</p>
                                            </div>
                                            <div>
                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                    {{ $t('Anchor URL') }} 2
                                                </label>
                                                <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url_2 || '-' }}</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <!-- Divider -->
                                    <Divider v-if="(item.content.anchor_name_2 || item.content.anchor_url_2) && (item.content.anchor_name_3 || item.content.anchor_url_3)" layout="vertical" class="hidden md:flex" />
                                    
                                    <!-- Anchor 3 -->
                                    <div v-if="item.content.anchor_name_3 || item.content.anchor_url_3" class="flex-1">
                                        <div class="space-y-2">
                                            <div>
                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                    {{ $t('Anchor Name') }} 3
                                                </label>
                                                <p class="text-sm text-blue-900">{{ item.content.anchor_name_3 || '-' }}</p>
                                            </div>
                                            <div>
                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                    {{ $t('Anchor URL') }} 3
                                                </label>
                                                <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url_3 || '-' }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Other Content Details -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div v-if="item.content">
                                    <label class="block text-sm font-medium text-surface-700 mb-1">
                                        {{ $t('Language') }}
                                    </label>
                                    <p class="text-surface-900">{{ item.content.language || '-' }}</p>
                                </div>

                                <div v-if="item.content">
                                    <label class="block text-sm font-medium text-surface-700 mb-1">
                                        {{ $t('Content Published At') }}
                                    </label>
                                    <p class="text-surface-900">
                                        {{ formatDate(item.content.sent_to_portal_at) }}
                                    </p>
                                </div>
                            </div>

                            <!-- Observations -->
                            <div v-if="item.observations" class="p-4 bg-amber-50 rounded-lg border border-amber-200">
                                <label class="block text-sm font-medium text-amber-900 mb-2">
                                    <i class="pi pi-info-circle mr-1"></i>
                                    {{ $t('Special Instructions') }}
                                </label>
                                <p class="text-surface-900">{{ item.observations }}</p>
                            </div>

                            <!-- Live Link Confirmed Status -->
                            <div v-if="item.live_link_confirmed_at" class="p-4 bg-green-50 rounded-lg border border-green-200">
                                <div class="flex flex-col gap-2">
                                    <div class="flex items-center gap-2 text-green-700">
                                        <i class="pi pi-check-circle text-xl"></i>
                                        <div>
                                            <p class="font-medium">{{ $t('Live link confirmed') }}</p>
                                            <p class="text-sm">
                                                {{ formatDate(item.live_link_confirmed_at) }}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="mt-2">
                                        <label class="block text-sm font-medium text-green-700 mb-1">
                                            {{ $t('Live Link URL') }}
                                        </label>
                                        <a
                                            :href="item.link"
                                            target="_blank"
                                            class="text-primary-500 hover:text-primary-600 underline break-all"
                                        >
                                            {{ item.link }}
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <!-- Live Link Input and Confirm Button -->
                            <div v-else class="space-y-3">
                                <div class="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <label class="block text-sm font-medium text-surface-700 mb-2">
                                        {{ $t('Enter Live Link URL') }} <span class="text-red-500">*</span>
                                    </label>
                                    <div class="relative">
                                        <InputText
                                            v-model="liveLinks[item.id]"
                                            type="url"
                                            placeholder="https://example.com/article"
                                            class="w-full"
                                            :class="{ 
                                                'p-invalid': page.props.errors?.link
                                            }"
                                        />
                                    </div>                              
                                    <small class="text-surface-500 mt-1 block">
                                        {{ $t('Enter the full URL where the content was published') }}
                                    </small>
                                    
                                    <!-- Error Messages -->
                                    <div v-if="page.props.errors?.link" class="mt-2">
                                        <small 
                                            v-for="(error, index) in (Array.isArray(page.props.errors.link) ? page.props.errors.link : [page.props.errors.link])" 
                                            :key="index"
                                            :class="{
                                                'text-amber-600': getErrorSeverity(error) === 'warn',
                                                'text-red-500': getErrorSeverity(error) === 'error'
                                            }"
                                            class="block font-medium"
                                        >
                                            <i 
                                                :class="{
                                                    'pi pi-exclamation-triangle text-amber-500': getErrorSeverity(error) === 'warn',
                                                    'pi pi-times-circle text-red-500': getErrorSeverity(error) === 'error'
                                                }"
                                                class="mr-1"
                                            ></i>
                                            {{ translateError(error) }}
                                        </small>
                                    </div>
                                </div>

                                <div class="flex justify-end gap-3">
                                    <Button
                                        :label="$t('Confirm Live Link')"
                                        icon="pi pi-link"
                                        severity="success"
                                        @click="confirmLiveLink(item.id)"
                                        :disabled="!liveLinks[item.id] || !liveLinks[item.id].trim()"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </Card>
        </div>

        <!-- All Confirmed Message -->
        <Card v-if="allConfirmed && hasPublishedContent" class="mt-6">
            <template #content>
                <div class="text-center py-8">
                    <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <i class="pi pi-check text-green-600 text-3xl"></i>
                    </div>
                    <h3 class="text-xl font-semibold text-surface-900 mb-2">
                        {{ $t('All live links confirmed!') }}
                    </h3>
                    <p class="text-surface-600">
                        {{ $t('Thank you! All live links have been confirmed. Your order is now complete.') }}
                    </p>
                </div>
            </template>
        </Card>
    </div>

    <!-- Error Modal -->
    <Dialog
        v-model:visible="showErrorModal"
        :header="$t('URL Validation Error')"
        :modal="true"
        :closable="true"
        :style="{ width: '30vw' }"
        :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
        @hide="closeErrorModal"
    >
        <div class="space-y-4">
            <div class="flex items-start gap-3">
                <div class="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <i class="pi pi-exclamation-triangle text-red-600 text-xl"></i>
                </div>
                <div class="flex-1">
                    <h3 class="text-lg font-semibold text-surface-900 mb-2">
                        {{ $t('Invalid URL') }}
                    </h3>
                    <p class="text-surface-700">
                        {{ errorMessage }}
                    </p>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end">
                <Button
                    :label="$t('Close')"
                    icon="pi pi-times"
                    severity="secondary"
                    outlined
                    @click="closeErrorModal"
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
.p-tag {
    font-weight: 600;
}
</style>